import React, { Component } from 'react'
import { Card, CardBody, CardHeader, Button, Container, Row, Col } from 'reactstrap'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'
import BlockWithLoader from '../Support/BlockWithLoader'
import { getUserPermissions } from '../../utils/userPermissions'
import { API_BASE_URL } from '../../utils/apiConfig'

const WPS_QUERY = gql`
  query WpsFetchQuery($wps_id: ID!) {
    wps(wps_id: $wps_id) {
      id
      no
      rev_no
      manufacturer
      release_date
      actual
      additional_info
      pqr_wpqr_wpqt
      welding_processes_names
      joint_types_names
      welding_positions_names
      pqr_wpqr_wpqt_names
      weld_types_names
      preparations_names
      metal_transfers_names
      shield_gases_names
      backing_gases_names
      welding_details_names
      shield_gas_flow_rate
      backing_gas_flow_rate
      gouging
      bevel
      backing
      preheat_temp
      post_weld_heat_operate
      interpass_temp
      post_heating
      preheat_maintenenace_temp
      standard {
        id
        name
      }
      used_base_materials {
        id
        drawing_symbol
        base_material_designation_name
        thickness
        diameter
        base_material {
          id
          name
          group
          subgroup
          standard
        }
        product_type {
          id
          name
        }
      }
      joint_design {
        id
        url
      }
      welding_sequence {
        id
        url
      }
      parameters {
        id
        run
        filler_material {
          id
          name
          standard {
            id
            name
            description
          }
        }
        filler_material_operate
        filler_material_size
        welding_process {
          id
          name
        }
        welding_positions {
          id
          name
        }
        welding_position_ids
        current
        voltage
        wire_feed
        travel_speed
        type_of_current_polarity {
          id
          name
        }
        heat_input
      }
    }
  }
`

class WpsView extends Component {
  parseFillerMaterial = (name) => {
    if (!name) return { material: '', classification: '', standard: '' }
    const parts = name.split(' - ')
    return {
      material: parts[0] || '',
      classification: parts[1] || '',
      standard: parts[2] || ''
    }
  }

  formatRange = (range) => {
    if (!range || range.length !== 2) return '-'
    const start = parseFloat(range[0]).toFixed(1)
    const end = parseFloat(range[1]).toFixed(1)
    return `${start} - ${end}`
  }

  formatArrayRange = (array) => {
    if (!array || array.length !== 2) return '-'
    const start = array[0] === '∞' || array[0] === 'Infinity' ? 'Infinity' : parseFloat(array[0]).toFixed(2)
    const end = array[1] === '∞' || array[1] === 'Infinity' ? 'Infinity' : parseFloat(array[1]).toFixed(2)
    return `${start} - ${end}`
  }

  render() {
    const { match, history } = this.props
    const wpsId = match.params.id

    return (
      <Query query={WPS_QUERY} variables={{ wps_id: wpsId }}>
        {({ loading, error, data }) => (
          <BlockWithLoader loading={loading} renderChildren={true}>
            {error && <div>Error: {error.message}</div>}
            {data && data.wps && (
              <Card>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <h4>WPS no: {data.wps.no}</h4>
                    <div>
                      <Button 
                        color="secondary"
                        onClick={() => history.push('/wps')}
                      >
                        <i className="fa fa-arrow-left" /> Back to List
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="text-center mb-4">
                    <h1>WELDING PROCEDURE SPECIFICATION (WPS)</h1>
                    <h2>{data.wps.standard.name}</h2>
                  </div>

                  <Container>
                    <Row className="mb-4">
                      <Col md="4">
                        <p>Manufacturer: <strong>{data.wps.manufacturer}</strong></p>
                        <p>PQR, WPQR, WPQT No: <strong>{data.wps.pqr_wpqr_wpqt.join(', ')}</strong></p>
                      </Col>
                      <Col md="4">
                        <p>No: <strong>{data.wps.no}</strong></p>
                        <p>Rev no: <strong>{data.wps.rev_no}</strong></p>
                      </Col>
                      <Col md="4">
                        <p>Release date: <strong>{data.wps.release_date}</strong></p>
                        <p>Actual: <strong>{data.wps.actual ? 'YES' : 'NO'}</strong></p>
                      </Col>
                    </Row>

                    {data.wps.used_base_materials.length > 0 && (
                      <Row className="mb-4">
                        <Col md="12">
                          <h5>IDENTIFICATION OF PARENT MATERIAL</h5>
                          <table className="table table-bordered table-sm">
                            <thead>
                              <tr>
                                <th style={{width: '5%'}}>Parent material</th>
                                <th style={{width: '20%'}}>Name/Grade</th>
                                <th style={{width: '20%'}}>Material group</th>
                                <th style={{width: '15%'}}>Standard</th>
                                <th style={{width: '20%'}}>Thickness</th>
                                <th style={{width: '20%'}}>Diameter</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.wps.used_base_materials.map((material, idx) => (
                                <tr key={idx}>
                                  <td>{material.drawing_symbol || `t${idx > 0 ? idx : ''}`}</td>
                                  <td>{material.base_material_designation_name}</td>
                                  <td>{material.base_material && material.base_material.subgroup || material.base_material && material.base_material.group || ''}</td>
                                  <td>{material.base_material && material.base_material.standard || ''}</td>
                                  <td>{this.formatArrayRange(material.thickness)}</td>
                                  <td>{this.formatArrayRange(material.diameter)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Col>
                      </Row>
                    )}

                    <Row className="mb-4">
                      <Col md="6">
                        <h5>Joint Design</h5>
                        {data.wps.joint_design && data.wps.joint_design.url && (
                          <img 
                            src={API_BASE_URL + data.wps.joint_design.url} 
                            alt="Joint Design" 
                            className="img-fluid"
                          />
                        )}
                      </Col>
                      <Col md="6">
                        <h5>Welding Sequence</h5>
                        {data.wps.welding_sequence && data.wps.welding_sequence.url && (
                          <img 
                            src={API_BASE_URL + data.wps.welding_sequence.url} 
                            alt="Welding Sequence" 
                            className="img-fluid"
                          />
                        )}
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col md="3">
                        <p>Joint types: <strong>{data.wps.joint_types_names}</strong></p>
                        <p>Mode of metal transfer: <strong>{data.wps.metal_transfers_names}</strong></p>
                        <p>Gouging: <strong>{data.wps.gouging && data.wps.gouging.join(', ')}</strong></p>
                        <p>Bevel: <strong>{data.wps.bevel && data.wps.bevel.join(', ')}</strong></p>
                      </Col>
                      <Col md="3">
                        <p>Weld types: <strong>{data.wps.weld_types_names}</strong></p>
                        <p>Preparation and cleaning: <strong>{data.wps.preparations_names}</strong></p>
                        <p>Backing: <strong>{data.wps.backing && data.wps.backing.join(', ')}</strong></p>
                      </Col>
                      <Col md="3">
                        <p>Welding positions: <strong>{data.wps.welding_positions_names}</strong></p>
                        <p>Shield gas: <strong>{data.wps.shield_gases_names}</strong></p>
                        <p>Backing gas: <strong>{data.wps.backing_gases_names}</strong></p>
                      </Col>
                      <Col md="3">
                        <p>Welding details: <strong>{data.wps.welding_details_names}</strong></p>
                        <p>Shield gas flow rate: <strong>{data.wps.shield_gas_flow_rate}</strong></p>
                        <p>Backing gas flow rate: <strong>{data.wps.backing_gas_flow_rate}</strong></p>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col md="12">
                        <h5>FILLER MATERIALS</h5>
                        {console.log('Parameters:', data.wps.parameters)}
                        <table className="table table-bordered table-sm">
                          <thead>
                            <tr>
                              <th>Run</th>
                              <th>Filler material</th>
                              <th>Classification</th>
                              <th>Standard</th>
                              <th>Group</th>
                              <th>Material operate</th>
                              <th>Material size</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.wps.parameters.map((param, idx) => {
                              const fillerInfo = this.parseFillerMaterial(param.filler_material && param.filler_material.name)
                              return (
                                <tr key={idx}>
                                  <td>{param.run}</td>
                                  <td>{fillerInfo.material}</td>
                                  <td>{fillerInfo.classification}</td>
                                  <td>{fillerInfo.standard}</td>
                                  <td>{param.filler_material && param.filler_material.standard && param.filler_material.standard.description || '-'}</td>
                                  <td>{param.filler_material_operate}</td>
                                  <td>{param.filler_material_size}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </Col>
                    </Row>



                    {data.wps.parameters.length > 0 && (
                      <Row className="mb-4">
                        <Col md="12">
                          <h5>WELDING PARAMETERS</h5>
                          <table className="table table-bordered table-sm">
                            <thead>
                              <tr>
                                <th>Run</th>
                                <th>Welding process</th>
                                <th>Welding positions</th>
                                <th>Filler material</th>
                                <th>Current [A]</th>
                                <th>Voltage [V]</th>
                                <th>Wire feed [m/min]</th>
                                <th>Travel speed [cm/min]</th>
                                <th>Polarity</th>
                                <th>Heat input [kJ/cm]</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.wps.parameters.map((param, idx) => {
                                const fillerInfo = this.parseFillerMaterial(param.filler_material && param.filler_material.name)
                                return (
                                  <tr key={idx}>
                                    <td>{param.run}</td>
                                    <td>{param.welding_process && param.welding_process.name || ''}</td>
                                    <td>{param.welding_positions && param.welding_positions.map(pos => pos.name).join(', ') || '-'}</td>
                                    <td>{fillerInfo.material}</td>
                                    <td>{this.formatRange(param.current)}</td>
                                    <td>{this.formatRange(param.voltage)}</td>
                                    <td>{this.formatRange(param.wire_feed)}</td>
                                    <td>{this.formatRange(param.travel_speed)}</td>
                                    <td>{param.type_of_current_polarity && param.type_of_current_polarity.name || ''}</td>
                                    <td>{this.formatRange(param.heat_input)}</td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </Col>
                      </Row>
                    )}

                    <Row className="mb-4">
                      <Col md="12">
                        <h5>HEAT TREATMENT</h5>
                      </Col>
                    </Row>
                    <Row className="mb-4">
                      <Col md="3">
                        <p>Preheat temperature: <strong>min {data.wps.preheat_temp}</strong></p>
                        <p>Post weld heat operate: <strong>{data.wps.post_weld_heat_operate}</strong></p>
                      </Col>
                      <Col md="3">
                        <p>Interpass temperature [°C]: <strong>max {data.wps.interpass_temp}</strong></p>
                      </Col>
                      <Col md="3">
                        <p>Post heating: <strong>{data.wps.post_heating}</strong></p>
                      </Col>
                      <Col md="3">
                        <p>Preheat maintenenace temperature [°C]: <strong>{data.wps.preheat_maintenenace_temp}</strong></p>
                      </Col>
                    </Row>

                    {data.wps.additional_info && (
                      <Row className="mb-4">
                        <Col md="12">
                          <h5>ADDITIONAL INFORMATION</h5>
                          <p>{data.wps.additional_info}</p>
                        </Col>
                      </Row>
                    )}
                  </Container>
                </CardBody>
              </Card>
            )}
          </BlockWithLoader>
        )}
      </Query>
    )
  }
}

export default WpsView


// WEBPACK FOOTER //
// ./src/components/Wps/WpsView.js